import { bootstrapApplication } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
  RouteReuseStrategy,
  provideRouter,
  withPreloading,
  PreloadAllModules,
} from '@angular/router';
import {
  IonicRouteStrategy,
  provideIonicAngular,
} from '@ionic/angular/standalone';

import { routes } from './app/app.routes';
import { AppComponent } from './app/app.component';
import { isDevMode } from '@angular/core';
import { provideServiceWorker } from '@angular/service-worker';
import { pageTransitionAnimations } from './app/page-transitions/page-transitions';
import { withNgxsReduxDevtoolsPlugin } from '@ngxs/devtools-plugin';
import { withNgxsRouterPlugin } from '@ngxs/router-plugin';
import { withNgxsStoragePlugin } from '@ngxs/storage-plugin';
import { provideStore } from '@ngxs/store';
import { UIState } from 'src/app/rxjs-store/ui';
import { AuthState } from 'src/app/store/auth';
import { AccountDetailsState } from 'src/app/rxjs-store/account/account-details';
import { TeamsState } from 'src/app/rxjs-store/account/teams';
import { AccountActivityState } from 'src/app/rxjs-store/account/account-activity';
import { TeamActivityState } from 'src/app/rxjs-store/account/team-activity';
import { NavigationBarsState } from 'src/app/rxjs-store/navigation-bars';
import { InvitesState } from 'src/app/rxjs-store/account/invites';
import { ActiveTeamState } from 'src/app/rxjs-store/account/active-team';
import { ProfileState } from 'src/app/rxjs-store/account/profile';
import { UsersState } from 'src/app/rxjs-store/admin/users';
import { AddUserState } from 'src/app/rxjs-store/forms/add-user';
import { StudentsState } from 'src/app/rxjs-store/admin/students';
import { AddStudentState } from 'src/app/rxjs-store/forms/add-student';
import { AddObservationState } from 'src/app/rxjs-store/forms/add-observation';
import { ObservationsState } from 'src/app/rxjs-store/admin/observations';
import { AcademyState } from './app/pages-old/workspace/academy/store/academy.state';
import { AddSkillState } from 'src/app/rxjs-store/forms/add-skill';
import { GoalsState } from 'src/app/rxjs-store/admin/goals';
import { ObjectivesState } from 'src/app/rxjs-store/admin/objectives/objectives.state';
import { MyStudentsState } from 'src/app/rxjs-store/my-blume/my-students';
import { MyGoalsState } from 'src/app/rxjs-store/my-blume/my-goals';
import { MyObjectivesState } from 'src/app/rxjs-store/my-blume/my-objectives';
import { MyRemindersState } from 'src/app/rxjs-store/my-blume/my-reminders';
import { MyObservationsState } from 'src/app/rxjs-store/my-blume/my-observations';
import { AcademyModulesState } from './app/store/academy-modules';
import { WorkshopsState } from './app/rxjs-store/workshops';
import { provideFirebaseApp, initializeApp, getApp } from '@angular/fire/app';
import {
  provideAuth,
  getAuth,
  initializeAuth,
  browserLocalPersistence,
  connectAuthEmulator,
} from '@angular/fire/auth';
import { provideFirestore, getFirestore } from '@angular/fire/firestore';
import { environment } from './environments/environment';
import { Capacitor } from '@capacitor/core';
import { provideHighcharts } from 'highcharts-angular';
import { provideFunctions, getFunctions, connectFunctionsEmulator } from '@angular/fire/functions';

// Enable App Check debug token for localhost development
// The debug token will be logged to console - register it in Firebase Console
// declare global {
//   interface Window {
//     FIREBASE_APPCHECK_DEBUG_TOKEN?: boolean | string;
//   }
// }

// Enable debug mode for non-production environments
// if (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')) {
// window.FIREBASE_APPCHECK_DEBUG_TOKEN = true;
// }

bootstrapApplication(AppComponent, {
  providers: [
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    provideAnimations(),
    provideIonicAngular({
      mode: 'ios',
      navAnimation: pageTransitionAnimations
    }),
    provideRouter(routes, withPreloading(PreloadAllModules)),
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWhenStable:30000',
    }),
    provideFirebaseApp(() => initializeApp(environment.firebase)),
    provideAuth(() => {
      if (Capacitor.isNativePlatform()) {
        return initializeAuth(getApp(), {
          persistence: browserLocalPersistence,
        });
      } else {
        const auth = getAuth();
        if (environment.emulator) {
          connectAuthEmulator(auth, 'http://localhost:9099');
        }
        return auth;
      }
    }),
    provideFirestore(() => getFirestore()),
    provideFunctions(() => getFunctions(getApp(), 'us-east4')),
    provideStore(
      [
        UIState,
        AuthState,
        AccountDetailsState,
        TeamsState,
        AccountActivityState,
        TeamActivityState,
        NavigationBarsState,
        InvitesState,
        ActiveTeamState,
        ProfileState,
        UsersState,
        AddUserState,
        StudentsState,
        AddStudentState,
        ObservationsState,
        AddObservationState,
        GoalsState,
        ObjectivesState,
        AcademyState,
        AddSkillState,
        MyStudentsState,
        MyGoalsState,
        MyObjectivesState,
        MyRemindersState,
        MyObservationsState,
        AcademyModulesState,
        WorkshopsState,
      ],
      withNgxsReduxDevtoolsPlugin(),
      withNgxsRouterPlugin(),
      withNgxsStoragePlugin({
        keys: [
          'accountDetails',
          'addPartner',
          'addUser',
          'addStudent',
          'addObservation',
          'addSkill',
          'workspaceUI',
          'blumeAdminUI',
          'activeTeam',
          'teams',
          'users',
          'students',
          'dataCollection',
        ], // Persist accountDetails, addPartner, addUser, addStudent, addObservation, addSkill, workspaceUI, blumeAdminUI, activeTeam, teams, users, students, adminSkills, and dataCollection states to local storage
      })
    ),
    // App Check - protects your backend resources from abuse
    // provideAppCheck(() => {
    //   const provider = new ReCaptchaV3Provider(environment.appCheck.recaptchaSiteKey);
    //   return initializeAppCheck(undefined, {
    //     provider,
    //     isTokenAutoRefreshEnabled: environment.appCheck.isTokenAutoRefreshEnabled,
    //   });
    // }),
    provideHighcharts({
      // Global chart options applied across all charts
      options: {
        exporting: {
          enabled: false,
        },
        credits: {
          enabled: false,
        },
        title: {
          text: undefined,
        },
        legend: {
          enabled: false,
        },
        accessibility: {
          announceNewData: {
            enabled: true
          }
        },
        yAxis: {
          title: {
            text: undefined,
          },
        },
        xAxis: {
          title: {
            text: undefined,
          },
        }
      },

      // Include Highcharts additional modules (e.g., exporting, accessibility) or custom themes
      modules: () => {
        return [
          import('highcharts/esm/modules/accessibility'),
          // import('highcharts/esm/modules/exporting'),
          // import('highcharts/esm/themes/sunset'),
        ];
      },
    }),
  ],
});
